import { baseApi } from '../../../shared/api/baseApi';
import type { BlockedDomainsPageDto, BlockedSourcesPageDto } from '../model/dto';
import { toBlockedDomainsPage, toBlockedSourcesPage } from '../model/mappers';
import type { BlockedDomain, BlockedSource, BlocksPage, BlocksQuery } from '../model/types';

const DOMAINS = '/api/admin/blocked_domains';
const SOURCES = '/api/admin/products/reason_blocking';

const ORG_BLOCKED_HOSTS = (orgId: string | number) => `/api/organizations/${orgId}/blocked-hosts`;
const ORG_BLOCKED_HOST = (orgId: string | number) => `/api/organizations/${orgId}/blocked-host`;

const TAG = 'BlockedDomains' as const;

export const blocksApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getBlockedDomains: build.query<BlocksPage<BlockedDomain>, BlocksQuery>({
            query: ({ pageNum, pageSize, query = '' }) => ({
                url: DOMAINS,
                params: { page_size: pageSize, page_num: pageNum, query },
            }),
            transformResponse: (response: BlockedDomainsPageDto) => toBlockedDomainsPage(response),
            providesTags: [{ type: TAG, id: 'LIST' }],
        }),

        getBlockedSources: build.query<BlocksPage<BlockedSource>, BlocksQuery>({
            query: ({ pageNum, pageSize, query = '' }) => ({
                url: SOURCES,
                params: { page_size: pageSize, page_num: pageNum, query },
            }),
            transformResponse: (response: BlockedSourcesPageDto) => toBlockedSourcesPage(response),
            providesTags: [{ type: TAG, id: 'SOURCES' }],
        }),

        addBlockedDomain: build.mutation<unknown, { link: string; reason: string }>({
            query: (body) => ({ url: DOMAINS, method: 'POST', body }),
            invalidatesTags: [{ type: TAG, id: 'LIST' }],
        }),

        updateBlockedDomain: build.mutation<unknown, { id: string; reason: string }>({
            query: ({ id, reason }) => ({ url: `${DOMAINS}/${id}`, method: 'PATCH', body: { reason } }),
            invalidatesTags: [{ type: TAG, id: 'LIST' }],
        }),

        removeBlockedDomain: build.mutation<unknown, string>({
            query: (id) => ({ url: `${DOMAINS}/${id}`, method: 'DELETE' }),
            invalidatesTags: [{ type: TAG, id: 'LIST' }],
        }),

        // ===== Стоп-лист организации (как «Заблокированные источники» на старом фронте) =====

        getOrgBlockedHosts: build.query<string[], string | number>({
            query: (orgId) => ({ url: ORG_BLOCKED_HOSTS(orgId), method: 'GET' }),
            transformResponse: (response: { blocked_hosts?: string[] }) =>
                Array.isArray(response?.blocked_hosts) ? response.blocked_hosts : [],
            providesTags: [{ type: TAG, id: 'ORG' }],
        }),

        blockOrgHost: build.mutation<unknown, { orgId: string | number; host: string }>({
            query: ({ orgId, host }) => ({
                url: ORG_BLOCKED_HOST(orgId),
                method: 'POST',
                body: { blocked_host: host },
            }),
            invalidatesTags: [{ type: TAG, id: 'ORG' }],
        }),

        unblockOrgHost: build.mutation<unknown, { orgId: string | number; host: string }>({
            query: ({ orgId, host }) => ({
                url: ORG_BLOCKED_HOST(orgId),
                method: 'DELETE',
                body: { blocked_host: host },
            }),
            invalidatesTags: [{ type: TAG, id: 'ORG' }],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetBlockedDomainsQuery,
    useGetBlockedSourcesQuery,
    useAddBlockedDomainMutation,
    useUpdateBlockedDomainMutation,
    useRemoveBlockedDomainMutation,
    useGetOrgBlockedHostsQuery,
    useBlockOrgHostMutation,
    useUnblockOrgHostMutation,
} = blocksApi;